import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { ChevronLeft, ChevronRight, ExpandMore, Replay as ReplayIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import Layout from "components/Layout";
import MajorJobsPanel from "containers/MajorJobsPanel";
import ConnectionsPanel from "containers/ConnectionsPanel";
import {
  useAddTasksToJobMutation,
  useCreateTasksBatchMutation,
  useDeleteTaskJobMutation,
  useDeleteTaskMutation,
  useGetBackendUsersQuery,
  useGetBotRegistryQuery,
  useGetTaskJobsQuery,
  useLazyGetTaskJobQuery,
  useLazyGetConnectionTasksQuery,
  useLazyGetBackendUserBotConnectionsQuery,
  useRunTaskMutation,
  useRunTasksBatchMutation,
  useSetTaskPendingMutation,
  useStopTaskMutation,
  useStopTasksBatchMutation,
  useSubmitTask2FACodeMutation,
  useTasksBatchDeleteMutation,
  useUpdateTaskJobMutation,
  useUpdateAdminTaskMutation,
  useUpdateConnectionMutation,
  adminUsersApi,
} from "features/adminUsers/adminUsers";
import { BotsManagementWsEvent, botsManagementWs } from "features/ws/botsManagementWs";
import { useAppDispatch } from "features/store";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AdminVendorConnection, BackendUser } from "types/interfaces/adminUsers";
import { buildDataStorageFolderMappingSeed } from "utils/dataStorageNavigation";
import { extractSkipErrorEmployees, applySkipErrorEmployees } from "utils/additionalOptions";
import { sanitizeEmployeesInput, parseEmployeesNumber } from "utils/employeesNumber";

const DIALOG_PAPER_NO_SPINNER_SX = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
} as const;

function normalizeBotRegistryRows(
  data: { results?: BotRegistryRow[] } | BotRegistryRow[] | undefined | null,
): BotRegistryRow[] {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

/** Display the backend user's own name (first/last), falling back to the username — never the client. */
function backendUserDisplayName(
  user: Pick<BackendUser, "first_name" | "last_name" | "username">,
): string {
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return fullName || user.username;
}

type BotRegistryRow = {
  vendor: string;
  job_kind: string;
  bot_slug: string;
  bot_id: string;
};

type ConnectionTask = {
  id: number;
  job?: number | null;
  task_status: string;
  bot_work_status: string;
  task2fa_status?: string;
  bot_name: string;
  document_type: string;
  employee_number_from: number;
  employee_number_to: number;
  browser_profile_id?: number;
};

type TaskJobSummary = {
  id: number;
  name?: string;
  bot_name?: string;
  document_type?: string;
  path_to_s3_base_folder?: string;
  data_storage_prefix?: string;
  data_storage_root_prefix?: string;
  data_storage_root_display_name?: string;
  upload_as_zip?: boolean;
  unzip_folder_before_uploading?: boolean;
  additional_options?: string | null;
  auto_recalculate_ranges?: boolean;
  task_status: string;
  employee_number_from: number;
  employee_number_to: number;
  actual_total_employees?: number | null;
  stats?: {
    total_tasks?: number;
    running_tasks?: number;
    finished_tasks?: number;
    pending_tasks?: number;
    error_tasks?: number;
    success_employees_count?: number;
    error_employees_count?: number;
    range_coverage?: {
      covered_from?: number | null;
      covered_to?: number | null;
      warning?: string;
    };
  };
};

/** Narrow strip when user list is collapsed — avoids `fit-content` width jumps during transition */
const USERS_PANEL_COLLAPSED_PX = 48;

const BATCH_DIALOG_LISTBOX_PROPS = { style: { maxHeight: 280, overflow: "auto" as const } };

const filterRegistryVendors = createFilterOptions<string>();

const filterRegistryBots = createFilterOptions<BotRegistryRow>({
  stringify: (option) => `${option.job_kind} ${option.bot_slug} ${option.job_kind}.${option.bot_slug}`,
});

type ChipColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

function taskRunStatusChipColor(status: string): ChipColor {
  const s = (status || "").toUpperCase();
  switch (s) {
    case "PENDING":
      return "warning";
    case "ECS_CONTAINER_STARTED":
      return "info";
    case "PROCESSING":
      return "primary";
    case "FINISHED":
      return "success";
    case "ERROR":
    case "CRITICAL_ERROR":
      return "error";
    case "STOPPED":
      return "warning";
    default:
      return "default";
  }
}

function botWorkStatusChipColor(status: string): ChipColor {
  const s = (status || "").toUpperCase();
  switch (s) {
    case "NOT_LOGGED_IN":
      return "default";
    case "2FA":
      return "warning";
    case "LOGGED_IN":
      return "info";
    case "PROCESSING":
      return "primary";
    case "FINISHED":
      return "success";
    case "ERROR":
    case "STOPPED":
      return "error";
    default:
      return "default";
  }
}

function StatusChip({
  label,
  color,
  title,
}: {
  label: string;
  color: ChipColor;
  title?: string;
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      color={color}
      title={title}
      sx={{
        height: 22,
        maxHeight: 22,
        fontWeight: 600,
        fontSize: "0.6875rem",
        lineHeight: 1,
        "& .MuiChip-label": { px: 0.65, py: 0 },
      }}
    />
  );
}

const ACCORDION_ROW_SX = {
  "&:before": { display: "none" },
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  mb: 1,
  overflow: "hidden",
  transition: "none",
  "& .MuiCollapse-root": {
    transition: "none",
  },
  "&.Mui-expanded": {
    margin: 0,
    mb: 1,
    mt: 0,
  },
} as const;

const ACCORDION_SUMMARY_SX = {
  minHeight: 48,
  py: 0,
  "&.Mui-expanded": {
    minHeight: 48,
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: "10px 0 !important",
    "&.Mui-expanded": {
      margin: "10px 0 !important",
    },
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    marginRight: 0,
  },
} as const;

/** Title | stats cluster (equal gaps inside) | actions */
const JOB_ACCORDION_SUMMARY_GRID_SX = {
  display: "grid",
  alignItems: "center",
  columnGap: 1.25,
  width: "100%",
  minWidth: { xs: 400, sm: "auto" },
  gridTemplateColumns: "minmax(0, 1fr) max-content auto",
} as const;

const JOB_ROW_STATS_CLUSTER_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  flexShrink: 0,
} as const;

const JOB_ROW_TASKS_WIDTH_PX = 116;
const JOB_ROW_EMPLOYEES_WIDTH_PX = 168;

export default function BotsManagementPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id, connectionId, majorJobId } = useParams<{
    id?: string;
    connectionId?: string;
    majorJobId?: string;
  }>();
  const location = useLocation();
  /** True when the user is on the dedicated bot-connections screen for the selected user. */
  const isConnectionsScreen = useMemo(
    () => /\/bots-management\/\d+\/connections\/?$/.test(location.pathname),
    [location.pathname],
  );
  const routedMajorJobPk =
    majorJobId != null && majorJobId !== "" && Number.isFinite(Number(majorJobId))
      ? Number(majorJobId)
      : null;
  const selectedConnectionId =
    connectionId != null && connectionId !== "" && Number.isFinite(Number(connectionId))
      ? Number(connectionId)
      : null;

  const [search, setSearch] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [loadedBackendUsers, setLoadedBackendUsers] = useState<BackendUser[]>([]);
  const [usersPanelOpen, setUsersPanelOpen] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const BOTS_MGMT_LAST_URL_KEY = "bots_management_last_url";

  // Restore last visited URL on first mount when no id is provided
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    if (!id || id === "") {
      const saved = localStorage.getItem(BOTS_MGMT_LAST_URL_KEY);
      if (saved) {
        navigate(saved, { replace: true });
      }
    }
  }, [id, navigate]);

  // Save the current meaningful URL (including ?tab=... and other search params) whenever navigation happens
  useEffect(() => {
    if (id && id !== "") {
      localStorage.setItem(BOTS_MGMT_LAST_URL_KEY, location.pathname + location.search);
    }
  }, [location.pathname, location.search, id]);

  useEffect(() => {
    if (id != null && id !== "") {
      const n = Number(id);
      setSelectedUserId(Number.isFinite(n) ? n : null);
    } else {
      setSelectedUserId(null);
    }
  }, [id]);

  const { data: backendUsersData, isLoading: usersLoading } = useGetBackendUsersQuery({
    page: usersPage,
    page_size: 50,
    search: search || undefined,
  });
  const backendUsers: BackendUser[] = loadedBackendUsers;
  const hasMoreBackendUsers = Boolean(backendUsersData?.next);

  const [
    loadBotConnections,
    {
      data: botConnectionsData,
      isLoading: isConnectionsLoadingInitial,
      isFetching: isConnectionsFetching,
      isError: connectionsError,
    },
  ] = useLazyGetBackendUserBotConnectionsQuery();
  const connectionsLoading = isConnectionsLoadingInitial || isConnectionsFetching;

  const selectedUser = useMemo(
    () => backendUsers.find((u) => u.id === selectedUserId) ?? null,
    [backendUsers, selectedUserId],
  );
  const botConnections: AdminVendorConnection[] = botConnectionsData?.connections ?? [];
  const selectedConnection = useMemo(
    () => botConnections.find((c) => c.id === selectedConnectionId) ?? null,
    [botConnections, selectedConnectionId],
  );
  const selectedConnectionNumber = useMemo(() => {
    if (selectedConnectionId == null) return null;
    const idx = botConnections.findIndex((c) => c.id === selectedConnectionId);
    return idx >= 0 ? idx + 1 : null;
  }, [botConnections, selectedConnectionId]);
  const [
    loadConnectionTasks,
    {
      data: tasksData,
      isLoading: isTasksLoadingInitial,
      isFetching: isTasksFetching,
      isError: tasksError,
    },
  ] = useLazyGetConnectionTasksQuery();
  const tasksLoading = isTasksLoadingInitial || isTasksFetching;
  const [runTask, { isLoading: isRunLoading }] = useRunTaskMutation();
  const [stopTask, { isLoading: isStopLoading }] = useStopTaskMutation();
  const [setTaskPending, { isLoading: isSetPendingLoading }] = useSetTaskPendingMutation();
  const [submitTask2FACode, { isLoading: isSubmit2FALoading }] = useSubmitTask2FACodeMutation();
  const [updateConnection, { isLoading: isConnectionSaving }] = useUpdateConnectionMutation();
  const [updateAdminTask] = useUpdateAdminTaskMutation();
  const [deleteTask, { isLoading: isTaskDeleting }] = useDeleteTaskMutation();
  const [addTasksToJob, { isLoading: isAddingTasks }] = useAddTasksToJobMutation();
  const [createTasksBatch, { isLoading: isBatchCreating }] = useCreateTasksBatchMutation();
  const [runTasksBatch, { isLoading: isBatchRunning }] = useRunTasksBatchMutation();
  const [stopTasksBatch, { isLoading: isBatchStopping }] = useStopTasksBatchMutation();
  const [deleteTaskJob, { isLoading: isJobDeleting }] = useDeleteTaskJobMutation();
  const [updateTaskJob, { isLoading: isJobSaving }] = useUpdateTaskJobMutation();
  const [loadTaskJobDetails] = useLazyGetTaskJobQuery();
  const [twoFATask, setTwoFATask] = useState<ConnectionTask | null>(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFAError, setTwoFAError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editConnectionId, setEditConnectionId] = useState<number | null>(null);
  const [editConnectionVendor, setEditConnectionVendor] = useState("");
  const [editConnectionUsername, setEditConnectionUsername] = useState("");
  const [editConnectionHost, setEditConnectionHost] = useState("");
  const [editConnectionClientCode, setEditConnectionClientCode] = useState("");
  const [editConnectionPassword, setEditConnectionPassword] = useState("");
  const [isConnectionPasswordVisible, setIsConnectionPasswordVisible] = useState(false);
  const [editConnectionError, setEditConnectionError] = useState<string | null>(null);
  const [addTasksOpen, setAddTasksOpen] = useState(false);
  const [addTasksJobId, setAddTasksJobId] = useState("");
  const [addTasksCount, setAddTasksCount] = useState("1");
  const [addTasksError, setAddTasksError] = useState<string | null>(null);
  const [deleteTaskId, setDeleteTaskId] = useState<number | null>(null);
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [batchTasksNumber, setBatchTasksNumber] = useState("2");
  const [batchEmployeesNumber, setBatchEmployeesNumber] = useState("100");
  const [batchName, setBatchName] = useState("");
  const [batchJobKind, setBatchJobKind] = useState("download_bots");
  const [batchBotName, setBatchBotName] = useState("download_bots.documents");
  const [batchAdditionalOptions, setBatchAdditionalOptions] = useState("");
  const [batchProcessErrors, setBatchProcessErrors] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [expandedJobIds, setExpandedJobIds] = useState<Record<number, boolean>>({});
  const [manageJobId, setManageJobId] = useState<number | null>(null);
  const [manageJobDetail, setManageJobDetail] = useState<TaskJobSummary | null>(null);
  const [batchRegistryVendor, setBatchRegistryVendor] = useState("");
  const [editJobId, setEditJobId] = useState<number | null>(null);
  const [editJobName, setEditJobName] = useState("");
  const [editJobKind, setEditJobKind] = useState("download_bots");
  const [editJobBotName, setEditJobBotName] = useState("");
  const [editJobEmployeeTo, setEditJobEmployeeTo] = useState("0");
  const [editJobRegistryVendor, setEditJobRegistryVendor] = useState("");
  const [editJobAdditionalOptions, setEditJobAdditionalOptions] = useState("");
  const [editJobProcessErrors, setEditJobProcessErrors] = useState(false);
  const [editJobError, setEditJobError] = useState<string | null>(null);
  const [deleteJobId, setDeleteJobId] = useState<number | null>(null);
  const [deleteJobLogs, setDeleteJobLogs] = useState(false);
  const [jobActionError, setJobActionError] = useState<string | null>(null);
  const [pickedTaskIds, setPickedTaskIds] = useState<Record<number, true>>({});
  const [bulkDeleteTasksOpen, setBulkDeleteTasksOpen] = useState(false);

  const bulkPickedIds = useMemo(() => Object.keys(pickedTaskIds).map(Number), [pickedTaskIds]);
  const [tasksBatchDelete, { isLoading: isBulkTasksDeleting }] = useTasksBatchDeleteMutation();
  const [refreshingJobRowIds, setRefreshingJobRowIds] = useState<Record<number, boolean>>({});
  const selectedUserIdRef = useRef<number | null>(selectedUserId);
  const selectedConnectionIdRef = useRef<number | null>(selectedConnectionId);
  const refetchTasksRef = useRef<() => Promise<unknown>>(async () => Promise.resolve());
  const refetchConnectionsRef = useRef<() => Promise<unknown>>(async () => Promise.resolve());
  const { data: jobsData, isFetching: jobsLoading, refetch: refetchJobs } = useGetTaskJobsQuery(
    { connectionId: selectedConnectionId ?? undefined },
    { skip: selectedConnectionId == null },
  );
  const taskJobs = useMemo(() => {
    if (Array.isArray(jobsData)) {
      return jobsData as TaskJobSummary[];
    }
    const paginatedJobs = jobsData as { results?: TaskJobSummary[] } | undefined;
    const results = paginatedJobs?.results;
    return Array.isArray(results) ? results : [];
  }, [jobsData]);
  const editJobBaselineFrom =
    editJobId != null ? taskJobs.find((j) => j.id === editJobId)?.employee_number_from ?? 0 : 0;
  const tasksForJobId = (jobId: number): ConnectionTask[] =>
    (tasksData?.tasks ?? []).filter((task: ConnectionTask) => task.job === jobId);
  const managedJob =
    manageJobId != null && manageJobDetail?.id === manageJobId ? manageJobDetail : null;

  const { data: batchBotRegistryData } = useGetBotRegistryQuery(
    {
      vendor: batchRegistryVendor || undefined,
      jobKind: batchJobKind,
      isActive: true,
      pageSize: 200,
    },
    { skip: !createBatchOpen || !batchRegistryVendor },
  );
  const { data: allRegistryBotsForVendors } = useGetBotRegistryQuery(
    { isActive: true, pageSize: 500 },
    { skip: !createBatchOpen && editJobId == null },
  );

  const { data: editJobBotRegistryData } = useGetBotRegistryQuery(
    {
      vendor: editJobRegistryVendor || undefined,
      jobKind: editJobKind,
      isActive: true,
      pageSize: 200,
    },
    { skip: editJobId == null || !editJobRegistryVendor },
  );
  const registryVendorsAll = useMemo(() => {
    const rows = normalizeBotRegistryRows(allRegistryBotsForVendors);
    return Array.from(new Set(rows.map((r) => r.vendor))).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [allRegistryBotsForVendors]);

  const batchBotOptions = normalizeBotRegistryRows(batchBotRegistryData);
  const editJobBotOptions = normalizeBotRegistryRows(editJobBotRegistryData);
  const botNameFromRegistry = (bot: { job_kind: string; bot_slug: string }) =>
    `${bot.job_kind}.${bot.bot_slug}`;
  const selectedBatchBot = useMemo(
    () => batchBotOptions.find((b) => botNameFromRegistry(b) === batchBotName) ?? null,
    [batchBotOptions, batchBotName],
  );
  const selectedEditJobBot = useMemo(
    () => editJobBotOptions.find((b) => botNameFromRegistry(b) === editJobBotName) ?? null,
    [editJobBotOptions, editJobBotName],
  );
  useEffect(() => {
    setUsersPage(1);
    setLoadedBackendUsers([]);
  }, [search]);

  useEffect(() => {
    const nextUsers = backendUsersData?.results ?? [];
    setLoadedBackendUsers((prev) => {
      if (usersPage === 1) return nextUsers;
      const existingIds = new Set(prev.map((user) => user.id));
      return [...prev, ...nextUsers.filter((user) => !existingIds.has(user.id))];
    });
  }, [backendUsersData?.results, usersPage]);
  const jobDisplayNumbers = useMemo(() => {
    return new Map(
      [...taskJobs]
        .sort((left, right) => left.id - right.id)
        .map((job, index) => [job.id, index + 1]),
    );
  }, [taskJobs]);
  const getJobDisplayName = (job: TaskJobSummary) => {
    const name = job.name?.trim();
    return name || `Job #${jobDisplayNumbers.get(job.id) ?? job.id}`;
  };
  const addTasksSelectedJob = taskJobs.find((job) => String(job.id) === addTasksJobId) ?? null;

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  useEffect(() => {
    selectedConnectionIdRef.current = selectedConnectionId;
  }, [selectedConnectionId]);

  useEffect(() => {
    if (manageJobId == null) setManageJobDetail(null);
  }, [manageJobId]);

  useEffect(() => {
    if (createBatchOpen && selectedConnection?.vendor) {
      setBatchRegistryVendor(selectedConnection.vendor);
    }
  }, [createBatchOpen, selectedConnection?.vendor]);

  useEffect(() => {
    refetchTasksRef.current = async () => {
      const cid = selectedConnectionIdRef.current;
      if (cid == null) return;
      await dispatch(
        adminUsersApi.endpoints.getConnectionTasks.initiate(cid, {
          subscribe: false,
          forceRefetch: true,
        }),
      ).unwrap();
    };
  }, [dispatch]);

  useEffect(() => {
    refetchConnectionsRef.current = async () => {
      if (selectedUserIdRef.current == null) return;
      await loadBotConnections(selectedUserIdRef.current, false);
    };
  }, [loadBotConnections]);

  useEffect(() => {
    if (selectedUserId == null) return;
    void loadBotConnections(selectedUserId, false);
  }, [selectedUserId, loadBotConnections]);

  useEffect(() => {
    setPickedTaskIds({});
  }, [selectedConnectionId]);

  useEffect(() => {
    setExpandedJobIds((prev) => {
      const next: Record<number, boolean> = {};
      for (const job of taskJobs) {
        next[job.id] = prev[job.id] ?? true;
      }
      return next;
    });
  }, [taskJobs]);

  useEffect(() => {
    setJobActionError(null);
  }, [selectedConnectionId]);

  useEffect(() => {
    if (selectedConnectionId == null) {
      setTwoFATask(null);
      setTwoFAError(null);
      setActionError(null);
      setTwoFACode("");
    }
  }, [selectedConnectionId]);

  useEffect(() => {
    if (botsManagementWs.isConnected()) {
      refetchTasksRef.current();
      refetchConnectionsRef.current();
    }
    const unsubscribeStatus = botsManagementWs.subscribeStatus((connected) => {
      if (connected) {
        refetchTasksRef.current();
        refetchConnectionsRef.current();
      }
    });
    const unsubscribeEvents = botsManagementWs.subscribe((payload: BotsManagementWsEvent) => {
      const task = payload.task;
      if (!task) return;
      const eventUserId = task.user_id == null ? null : Number(task.user_id);
      const selectedUser = selectedUserIdRef.current == null ? null : Number(selectedUserIdRef.current);
      if (selectedUser == null) return;
      if (eventUserId != null && eventUserId !== selectedUser) return;
      if (
        selectedConnectionIdRef.current != null &&
        task.connection_id != null &&
        Number(task.connection_id) !== Number(selectedConnectionIdRef.current)
      ) {
        return;
      }
      refetchTasksRef.current();
      refetchConnectionsRef.current();
    });
    return () => {
      unsubscribeStatus();
      unsubscribeEvents();
    };
  }, []);

  const handleSelectUser = (userId: number) => {
    navigate(`/bots-management/${userId}`);
  };

  const openConnectionEditor = (connection: AdminVendorConnection) => {
    setEditConnectionError(null);
    setEditConnectionId(connection.id);
    setEditConnectionVendor(connection.vendor || "");
    setEditConnectionUsername(connection.username || "");
    setEditConnectionHost(connection.host || "");
    setEditConnectionClientCode(connection.client_code || "");
    setEditConnectionPassword(connection.password || "");
    setIsConnectionPasswordVisible(false);
  };

  const openAddTasksDialog = (job: TaskJobSummary) => {
    setAddTasksError(null);
    setAddTasksJobId(String(job.id));
    setAddTasksCount("1");
    setAddTasksOpen(true);
  };

  const openJobEditor = (job: TaskJobSummary) => {
    setEditJobError(null);
    setEditJobId(job.id);
    setEditJobName(job.name || "");
    setEditJobBotName(job.bot_name || "");
    setEditJobKind((job.bot_name || "download_bots").split(".")[0] || "download_bots");
    setEditJobEmployeeTo(String(job.employee_number_to ?? 0));
    setEditJobRegistryVendor(selectedConnection?.vendor || "");
    const { processErrors, remaining } = extractSkipErrorEmployees(job.additional_options || "");
    setEditJobProcessErrors(processErrors);
    setEditJobAdditionalOptions(remaining);
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    const err = error as
      | { data?: { message?: string; detail?: string; error?: string } }
      | undefined;
    return (
      err?.data?.message ||
      err?.data?.detail ||
      err?.data?.error ||
      fallback
    );
  };

  const canRunTaskByStatus = (status: string) =>
    ["PENDING", "STOPPED", "FINISHED", "ERROR", "CRITICAL_ERROR"].includes(status);

  const isTaskRunning = (task: ConnectionTask) => !canRunTaskByStatus(task.task_status);
  const shouldShowPass2FA = (task: ConnectionTask) => {
    const task2fa = (task.task2fa_status || "").toLowerCase();
    return task.bot_work_status === "2FA" && ["pending", "ready"].includes(task2fa);
  };

  const extractDefaultFolderName = (botName: string) => {
    const parts = botName.split(".");
    return parts[parts.length - 1] || "data";
  };

  /** Canonical vendor folder slug under S3 is users/{id}/{vendor}/data/{slug}. */
  const parseBotStoragePath = (currentPath: string, taskVendor: string) => {
    const uid = String(selectedUserId ?? "user");
    const defaultPrefix = `storage_bucket/${uid}/${taskVendor}/data`;
    const norm = (currentPath || "").trim();
    if (!norm) {
      return { prefix: defaultPrefix, folderRest: "" };
    }
    const segs = norm.split("/").filter(Boolean);
    let u: string;
    let v: string;
    let rest: string[];
    if (segs[0] === "users" && segs.length >= 3) {
      u = segs[1];
      v = segs[2];
      rest = segs.slice(3);
    } else if (segs[0] === "storage_bucket" && segs.length >= 3) {
      u = segs[1];
      v = segs[2];
      rest = segs.slice(3);
    } else {
      return { prefix: defaultPrefix, folderRest: "" };
    }
    let folderSlug = "";
    if (rest.length && rest[0] === "data") {
      folderSlug = rest.slice(1).join("/");
    } else {
      folderSlug = rest.join("/");
    }
    return { prefix: `storage_bucket/${u}/${v}/data`, folderRest: folderSlug };
  };

  const buildPathPrefix = (currentPath: string, taskVendor: string) =>
    parseBotStoragePath(currentPath, taskVendor).prefix;

  const handleRunTask = async (task: ConnectionTask) => {
    setActionError(null);
    try {
      if (task.task_status !== "PENDING") {
        await setTaskPending(task.id).unwrap();
      }
      await runTask(task.id).unwrap();
      setTwoFAError(null);
      setTwoFACode("");
      await refetchTasksRef.current();
    } catch (error) {
      setActionError(getErrorMessage(error, "Failed to run task."));
    }
  };

  const handleStopTask = async (taskId: number): Promise<boolean> => {
    setActionError(null);
    try {
      await stopTask(taskId).unwrap();
      if (twoFATask?.id === taskId) {
        setTwoFATask(null);
        setTwoFACode("");
        setTwoFAError(null);
      }
      await refetchTasksRef.current();
      return true;
    } catch (error) {
      setActionError(getErrorMessage(error, "Failed to stop task."));
      return false;
    }
  };

  const handleSubmit2FA = async () => {
    if (twoFATask == null) return;
    const submittedTaskId = twoFATask.id;
    const trimmedCode = twoFACode.trim();
    if (!trimmedCode) {
      setTwoFAError("2FA code is required.");
      return;
    }
    setTwoFAError(null);
    try {
      await submitTask2FACode({ taskId: submittedTaskId, code: trimmedCode }).unwrap();
      setTwoFATask(null);
      setTwoFACode("");
      await refetchTasksRef.current();
      if (selectedConnectionIdRef.current != null) {
        for (let attempt = 0; attempt < 6; attempt += 1) {
          const queryResult = await loadConnectionTasks(selectedConnectionIdRef.current, false);
          const tasks =
            (queryResult as { data?: { tasks?: ConnectionTask[] } })?.data?.tasks ?? [];
          const currentTask = tasks.find((task) => task.id === submittedTaskId);
          if (!currentTask || currentTask.bot_work_status !== "2FA") {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 700));
        }
      }
    } catch (error) {
      setTwoFAError(getErrorMessage(error, "Failed to submit 2FA code."));
    }
  };

  const handleSaveConnection = async () => {
    if (editConnectionId == null) return;
    try {
      const body: Partial<AdminVendorConnection> = {
        vendor: editConnectionVendor.trim(),
        username: editConnectionUsername.trim(),
        host: editConnectionHost.trim() || null,
        client_code: editConnectionClientCode.trim() || null,
        password: editConnectionPassword,
        port: "9223",
      };
      if (!body.vendor || !body.username) {
        setEditConnectionError("Vendor and username are required.");
        return;
      }
      await updateConnection({ connectionId: editConnectionId, body }).unwrap();
      await refetchConnectionsRef.current();
      setEditConnectionId(null);
      setEditConnectionVendor("");
      setEditConnectionUsername("");
      setEditConnectionHost("");
      setEditConnectionClientCode("");
      setEditConnectionPassword("");
      setIsConnectionPasswordVisible(false);
      setEditConnectionError(null);
    } catch (error) {
      setEditConnectionError(getErrorMessage(error, "Failed to update connection."));
    }
  };

  const handleAddTasks = async () => {
    const jobPk = Number.parseInt(addTasksJobId, 10);
    if (!Number.isFinite(jobPk) || selectedConnectionId == null || selectedConnection == null) {
      return;
    }
    setAddTasksError(null);
    const n = Number.parseInt(String(addTasksCount).trim(), 10);
    if (!Number.isFinite(n) || n < 1) {
      setAddTasksError("Count must be at least 1.");
      return;
    }
    if (n > 30) {
      setAddTasksError("You can add at most 30 tasks at once.");
      return;
    }
    try {
      await addTasksToJob({ jobId: jobPk, count: n }).unwrap();
      await refetchTasksRef.current();
      await refetchJobs();
      setAddTasksOpen(false);
      setAddTasksJobId("");
    } catch (error) {
      setAddTasksError(getErrorMessage(error, "Failed to add tasks."));
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    setActionError(null);
    try {
      await deleteTask(taskId).unwrap();
      await refetchTasksRef.current();
      setDeleteTaskId(null);
    } catch (error) {
      setActionError(getErrorMessage(error, "Failed to delete task."));
    }
  };

  const togglePickTask = (taskId: number, on: boolean) => {
    setPickedTaskIds((prev) => {
      const next = { ...prev };
      if (on) next[taskId] = true as const;
      else delete next[taskId];
      return next;
    });
  };

  const togglePickTasksForJob = (jobTasksList: ConnectionTask[], on: boolean) => {
    setPickedTaskIds((prev) => {
      const next = { ...prev };
      for (const task of jobTasksList) {
        if (on) next[task.id] = true as const;
        else delete next[task.id];
      }
      return next;
    });
  };

  const handleBulkDeleteTasksConfirmed = async () => {
    if (bulkPickedIds.length === 0) return;
    setJobActionError(null);
    try {
      await tasksBatchDelete({ task_ids: bulkPickedIds }).unwrap();
      setPickedTaskIds({});
      setBulkDeleteTasksOpen(false);
      await refetchTasksRef.current();
      await refetchJobs();
    } catch (error) {
      setJobActionError(getErrorMessage(error, "Failed to delete tasks in batch."));
    }
  };

  const handleCreateBatch = async () => {
    if (selectedConnectionId == null || selectedConnection == null || selectedUserId == null) return;
    setBatchError(null);
    setJobActionError(null);
    const tasksNumber = Number.parseInt(String(batchTasksNumber).trim(), 10);
    if (!Number.isFinite(tasksNumber) || tasksNumber < 1) {
      setBatchError("Tasks number must be a positive integer.");
      return;
    }
    const employeesNumber = parseEmployeesNumber(batchEmployeesNumber);
    if (employeesNumber == null) {
      setBatchError("Inclusive last employee index must be a positive integer.");
      return;
    }
    if (!batchRegistryVendor.trim()) {
      setBatchError("Select a vendor for the bot list.");
      return;
    }
    if (batchRegistryVendor !== (selectedConnection.vendor ?? "")) {
      setBatchError(
        `Registry vendor must match this connection (${selectedConnection.vendor}). Use that vendor or open another connection.`,
      );
      return;
    }
    const botName = batchBotName.trim();
    if (!botName) {
      setBatchError("Bot field is required.");
      return;
    }
    if (!batchName.trim()) {
      setBatchError("Job name is required.");
      return;
    }
    const defaultPath = `users/${selectedUserId}/${selectedConnection.vendor}/data/${extractDefaultFolderName(botName)}`;
    try {
      const result = await createTasksBatch({
        connection: selectedConnectionId,
        bot_name: botName,
        document_type: selectedConnection.vendor,
        task_status: "PENDING",
        bot_work_status: "NOT_LOGGED_IN",
        path_to_s3_base_folder: defaultPath,
        browser_profile_id: 1,
        upload_as_zip: false,
        unzip_folder_before_uploading: false,
        tasks_number: tasksNumber,
        employees_number: employeesNumber,
        employee_number_from: 0,
        use_separate_profiles: true,
        create_job: true,
        job_name: batchName.trim(),
        additional_options: applySkipErrorEmployees(batchAdditionalOptions.trim(), batchProcessErrors) || null,
      }).unwrap();
      await Promise.all([refetchTasksRef.current(), refetchJobs()]);
      const createdJobId = (result as { job?: { id?: number } }).job?.id;
      if (createdJobId != null) {
        setExpandedJobIds((prev) => ({ ...prev, [createdJobId]: true }));
      }
      setBatchAdditionalOptions("");
      setBatchProcessErrors(false);
      setCreateBatchOpen(false);
    } catch (error) {
      setBatchError(getErrorMessage(error, "Failed to create job batch."));
    }
  };

  const handleDeleteJob = async (jobId: number) => {
    setJobActionError(null);
    try {
      await deleteTaskJob({ jobId, deleteLogs: deleteJobLogs }).unwrap();
      setExpandedJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      setManageJobId(null);
      setManageJobDetail(null);
      setDeleteJobId(null);
      setDeleteJobLogs(false);
      await refetchTasksRef.current();
      await refetchJobs();
    } catch (error) {
      setJobActionError(getErrorMessage(error, "Failed to delete job."));
    }
  };

  const handleSaveJob = async () => {
    if (editJobId == null) return;
    setEditJobError(null);
    const editedJobSnapshot = taskJobs.find((j) => j.id === editJobId);
    const employeeFrom = editedJobSnapshot?.employee_number_from ?? 0;
    const employeeTo = parseEmployeesNumber(editJobEmployeeTo);
    const botName = editJobBotName.trim();
    if (!botName) {
      setEditJobError("Bot field is required.");
      return;
    }
    if (employeeTo == null) {
      setEditJobError("Employee To must be a positive integer.");
      return;
    }
    if (employeeTo < employeeFrom) {
      setEditJobError("Employee To must be >= Employee From.");
      return;
    }
    if (!selectedUserId || !selectedConnection?.vendor) {
      setEditJobError("Missing user or connection context.");
      return;
    }
    if (editJobRegistryVendor.trim() !== selectedConnection.vendor) {
      setEditJobError("Vendor must match this connection.");
      return;
    }
    try {
      const defaultPath = `users/${selectedUserId}/${selectedConnection.vendor}/data/${extractDefaultFolderName(botName)}`;
      const jobIdSnapshot = editJobId;
      await updateTaskJob({
        jobId: jobIdSnapshot!,
        body: {
          name: editJobName.trim(),
          bot_name: botName,
          document_type: selectedConnection.vendor,
          path_to_s3_base_folder: defaultPath,
          additional_options: applySkipErrorEmployees(editJobAdditionalOptions.trim(), editJobProcessErrors) || null,
          employee_number_from: employeeFrom,
          employee_number_to: employeeTo,
        },
      }).unwrap();

      const ordered = [...tasksForJobId(jobIdSnapshot)].sort((a, b) => a.id - b.id);
      if (ordered.length > 0) {
        try {
          await Promise.all(
            ordered.map((t, i) =>
              updateAdminTask({
                taskId: t.id,
                body: {
                  browser_profile_id: i + 1,
                  recalculate_job_ranges_after_save: false,
                },
              }).unwrap(),
            ),
          );
        } catch (taskUpdateErr) {
          setEditJobError(
            getErrorMessage(taskUpdateErr, "Job saved but failed to update task browser profiles."),
          );
          await refetchTasksRef.current();
          await refetchJobs();
          return;
        }
      }

      await refetchTasksRef.current();
      await refetchJobs();
      setEditJobId(null);
    } catch (error) {
      setEditJobError(getErrorMessage(error, "Failed to update job."));
    }
  };

  const handleRunJob = async (jobId: number) => {
    setJobActionError(null);
    try {
      await runTasksBatch({ job_id: jobId }).unwrap();
      await refetchTasksRef.current();
      await refetchJobs();
    } catch (error) {
      setJobActionError(getErrorMessage(error, "Failed to run job."));
    }
  };

  const handleStopJob = async (jobId: number) => {
    setJobActionError(null);
    try {
      const result = await stopTasksBatch({ job_id: jobId }).unwrap();
      setJobActionError(result.message);
      await refetchTasksRef.current();
      await refetchJobs();
    } catch (error) {
      setJobActionError(getErrorMessage(error, "Failed to stop job."));
    }
  };

  const handleRefreshJobRow = async (jobId: number) => {
    if (selectedConnectionId == null) return;
    setJobActionError(null);
    setRefreshingJobRowIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      await dispatch(
        adminUsersApi.endpoints.getConnectionTasks.initiate(selectedConnectionId, {
          subscribe: false,
          forceRefetch: true,
        }),
      ).unwrap();
      await refetchJobs();
      try {
        const details = await loadTaskJobDetails(jobId).unwrap();
        const warning = (
          details as { stats?: { range_coverage?: { warning?: string } } }
        ).stats?.range_coverage?.warning;
        if (warning) setJobActionError(warning);
        if (manageJobId === jobId) setManageJobDetail(details as unknown as TaskJobSummary);
      } catch {
        await refetchJobs();
      }
    } catch (error) {
      const errStatus = (error as { status?: number })?.status;
      if (errStatus === 404) await refetchJobs();
      else setJobActionError(getErrorMessage(error, "Failed to refresh job data."));
    } finally {
      setRefreshingJobRowIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  const handleViewJobFiles = (job: TaskJobSummary) => {
    const prefix = String(job.data_storage_prefix ?? job.path_to_s3_base_folder ?? "").trim();
    const rootPrefix = String(job.data_storage_root_prefix ?? "").trim();
    const rootDisplay = String(job.data_storage_root_display_name ?? "").trim();
    const segs = prefix.split("/").filter(Boolean);
    let leaf = segs.length ? (segs[segs.length - 1] ?? "") : "";
    if (!leaf || leaf === "data") {
      const parts = (job.bot_name ?? "").split(".");
      leaf = parts[parts.length - 1] || "data";
    }
    const folderMappingSeed =
      rootPrefix && rootDisplay
        ? buildDataStorageFolderMappingSeed(rootDisplay, rootPrefix, prefix)
        : undefined;
    navigate("/data-storage", {
      state: {
        initialPrefix: prefix,
        initialFname: leaf || getJobDisplayName(job),
        ...(folderMappingSeed ? { folderMappingSeed } : {}),
        fromBotsManagement: true,
      },
    });
  };

  const openManageJob = (job: TaskJobSummary) => {
    setManageJobId(job.id);
    setManageJobDetail(job);
    void loadTaskJobDetails(job.id)
      .unwrap()
      .then((raw) => setManageJobDetail(raw as unknown as TaskJobSummary))
      .catch(() => {});
  };

  const employeeProgressSummary = (job: TaskJobSummary) => {
    const processed =
      (job.stats?.success_employees_count ?? 0) + (job.stats?.error_employees_count ?? 0);
    const total = job.actual_total_employees;
    if (processed === 0 && (total === undefined || total === null)) return "unknown";
    if (total === undefined || total === null) return `${processed}/unknown`;
    return `${processed}/${total}`;
  };

  return (
    <Layout title="Bots Management" content="Bots Management">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          py: 3,
          px: { xs: 1.5, sm: 3 },
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
          "& input[type=number]": { MozAppearance: "textfield" },
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
        }}
      >
        <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
          Bots Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
          <Paper
            variant="outlined"
            sx={(theme) => ({
              width: usersPanelOpen ? { xs: 280, md: 340 } : USERS_PANEL_COLLAPSED_PX,
              flexShrink: 0,
              alignSelf: usersPanelOpen ? "stretch" : "flex-start",
              p: usersPanelOpen ? 1.5 : 0.5,
              display: "flex",
              flexDirection: "column",
              minHeight: usersPanelOpen ? 0 : "auto",
              overflow: "hidden",
              transition: theme.transitions.create(["width", "padding"], {
                easing: theme.transitions.easing.easeInOut,
                duration: 280,
              }),
              willChange: "width",
            })}
          >
            {usersPanelOpen ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search users..."
                    value={search}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSearch(e.target.value)
                    }
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <Tooltip title="Collapse user list" placement="right">
                    <IconButton
                      onClick={() => setUsersPanelOpen(false)}
                      aria-label="Collapse user list"
                      aria-expanded={true}
                      sx={(theme) => ({
                        flexShrink: 0,
                        padding: "6px",
                        color: theme.palette.primary.main,
                        backgroundColor: "transparent",
                        transition: theme.transitions.create(["color", "background-color", "transform"], {
                          duration: theme.transitions.duration.shorter,
                        }),
                        "&:hover": {
                          color: theme.palette.primary.dark,
                          backgroundColor: theme.palette.action.hover,
                          transform: "scale(1.08)",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.5rem",
                        },
                      })}
                    >
                      <ChevronLeft />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                  onScroll={(event) => {
                    const element = event.currentTarget;
                    const nearBottom =
                      element.scrollTop + element.clientHeight >= element.scrollHeight - 48;
                    if (nearBottom && hasMoreBackendUsers && !usersLoading) {
                      setUsersPage((page) => page + 1);
                    }
                  }}
                >
                  {usersLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List disablePadding>
                      {backendUsers.map((user: BackendUser) => (
                        <ListItemButton
                          key={user.id}
                          selected={selectedUserId === user.id}
                          onClick={() => handleSelectUser(user.id)}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={backendUserDisplayName(user)}
                            primaryTypographyProps={{ fontWeight: 700 }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                  pt: 0.25,
                }}
              >
                <Tooltip title="Expand user list" placement="right">
                  <IconButton
                    onClick={() => setUsersPanelOpen(true)}
                    aria-label="Expand user list"
                    aria-expanded={false}
                    sx={(theme) => ({
                      padding: "6px",
                      color: theme.palette.primary.main,
                      backgroundColor: "transparent",
                      transition: theme.transitions.create(["color", "background-color", "transform"], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      "&:hover": {
                        color: theme.palette.primary.dark,
                        backgroundColor: theme.palette.action.hover,
                        transform: "scale(1.08)",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.5rem",
                      },
                    })}
                  >
                    <ChevronRight />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              p: 1.5,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {selectedUser == null ? (
              <Typography color="text.secondary">
                Select a user to manage major jobs.
              </Typography>
            ) : selectedConnectionId != null ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Connection {selectedConnectionNumber != null ? `#${selectedConnectionNumber}` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setCreateBatchOpen(true)}>
                      Create job
                    </Button>
                    {selectedConnection && (
                      <Button size="small" variant="outlined" onClick={() => openConnectionEditor(selectedConnection)}>
                        Edit connection
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={() => navigate(`/bots-management/${selectedUser.id}`)}
                    >
                      Back to major jobs
                    </Button>
                  </Box>
                </Box>
                {selectedConnection && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography fontWeight={700}>{selectedConnection.vendor}</Typography>
                    <Typography color="text.secondary">@{selectedConnection.username}</Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  {actionError && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {actionError}
                    </Alert>
                  )}
                  {jobActionError && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {jobActionError}
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Jobs
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={bulkPickedIds.length === 0 || isBulkTasksDeleting}
                      onClick={() => setBulkDeleteTasksOpen(true)}
                    >
                      Delete selected tasks ({bulkPickedIds.length})
                    </Button>
                  </Box>
                  {jobsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                      <CircularProgress size={18} />
                    </Box>
                  ) : taskJobs.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      No jobs yet for this connection.
                    </Typography>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      {taskJobs.map((job) => {
                        const jobTasks = tasksForJobId(job.id);
                        return (
                          <Accordion
                            key={job.id}
                            expanded={expandedJobIds[job.id] ?? true}
                            disableGutters
                            elevation={0}
                            onChange={(_, expanded) =>
                              setExpandedJobIds((prev) => ({ ...prev, [job.id]: expanded }))
                            }
                            sx={ACCORDION_ROW_SX}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMore sx={{ color: "primary.main", fontSize: 26, opacity: 1 }} />
                              }
                              sx={{
                                ...ACCORDION_SUMMARY_SX,
                                px: 2,
                                "& .MuiAccordionSummary-expandIconWrapper": {
                                  color: "primary.main",
                                },
                              }}
                            >
                              <Box sx={{ overflowX: { xs: "auto", sm: "visible" }, width: "100%", py: 0 }}>
                                <Box sx={JOB_ACCORDION_SUMMARY_GRID_SX}>
                                  <Typography
                                    fontWeight={700}
                                    sx={{
                                      minWidth: 0,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontSize: "0.9375rem",
                                    }}
                                  >
                                    {getJobDisplayName(job)}
                                  </Typography>
                                  <Box sx={JOB_ROW_STATS_CLUSTER_SX}>
                                    <StatusChip
                                      label={job.task_status}
                                      color={taskRunStatusChipColor(job.task_status)}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        width: JOB_ROW_TASKS_WIDTH_PX,
                                        flexShrink: 0,
                                        fontVariantNumeric: "tabular-nums",
                                        fontSize: "0.8125rem",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      Tasks: {job.stats?.running_tasks ?? 0}/{job.stats?.total_tasks ?? 0}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        width: JOB_ROW_EMPLOYEES_WIDTH_PX,
                                        flexShrink: 0,
                                        fontSize: "0.8125rem",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={employeeProgressSummary(job)}
                                    >
                                      Employees: {employeeProgressSummary(job)}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.25,
                                      justifySelf: "end",
                                      flexShrink: 0,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <Tooltip title="Refresh job statistics and latest task statuses for this connection">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            void handleRefreshJobRow(job.id);
                                          }}
                                          disabled={Boolean(refreshingJobRowIds[job.id])}
                                          aria-label="Refresh job statistics and task statuses"
                                          sx={{
                                            color: "primary.main",
                                            "&:hover": { color: "primary.dark", bgcolor: "action.hover" },
                                          }}
                                        >
                                          <ReplayIcon sx={{ fontSize: 22 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openManageJob(job);
                                      }}
                                    >
                                      Manage
                                    </Button>
                                  </Box>
                              </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                pt: 1.5,
                                pb: 2,
                                px: 2,
                                borderTop: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography variant="body2">
                                Range: {job.employee_number_from}-{job.employee_number_to}; covered: {job.stats?.range_coverage?.covered_from ?? "n/a"}-{job.stats?.range_coverage?.covered_to ?? "n/a"}
                              </Typography>
                              {job.stats?.range_coverage?.warning && (
                                <Typography variant="caption" color="warning.main">
                                  {job.stats.range_coverage.warning}
                                </Typography>
                              )}
                              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1, textAlign: "center" }}>
                                Tasks
                              </Typography>
                              {tasksLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                  <CircularProgress size={22} />
                                </Box>
                              ) : tasksError ? (
                                <Typography color="error" variant="body2">
                                  Failed to load tasks for this connection.
                                </Typography>
                              ) : (
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        padding="checkbox"
                                        sx={{ fontWeight: 700, textAlign: "center", verticalAlign: "middle", width: 48 }}
                                      >
                                        <Checkbox
                                          size="small"
                                          indeterminate={
                                            jobTasks.some((task: ConnectionTask) => pickedTaskIds[task.id]) &&
                                            !jobTasks.every((task: ConnectionTask) => pickedTaskIds[task.id])
                                          }
                                          checked={
                                            jobTasks.length > 0 &&
                                            jobTasks.every((task: ConnectionTask) => pickedTaskIds[task.id])
                                          }
                                          disabled={jobTasks.length === 0}
                                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            togglePickTasksForJob(jobTasks, e.target.checked)
                                          }
                                          aria-label={`Select every task under job ${job.id}`}
                                        />
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          width: 80,
                                          textAlign: "center",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        No.
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 700, textAlign: "center", verticalAlign: "middle" }}>
                                        Bot
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          width: 148,
                                          minWidth: 148,
                                          textAlign: "center",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        Task Status
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          width: 148,
                                          minWidth: 148,
                                          textAlign: "center",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        Bot Status
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 700, textAlign: "center", verticalAlign: "middle" }}>
                                        Employees
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          width: 420,
                                          minWidth: 420,
                                          textAlign: "center",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        Actions
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {jobTasks.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                                          <Typography color="text.secondary">
                                            No tasks for this job.
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      jobTasks.map((task: ConnectionTask, index: number) => (
                                        <TableRow key={task.id} hover>
                                          <TableCell padding="checkbox" sx={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <Checkbox
                                              size="small"
                                              checked={Boolean(pickedTaskIds[task.id])}
                                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                togglePickTask(task.id, e.target.checked)
                                              }
                                              aria-label={`Select task ${task.id}`}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                                            {index + 1}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                                            {task.bot_name}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                              <StatusChip
                                                label={task.task_status}
                                                color={taskRunStatusChipColor(task.task_status)}
                                              />
                                            </Box>
                                          </TableCell>
                                          <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                              <StatusChip
                                                label={task.bot_work_status}
                                                color={botWorkStatusChipColor(task.bot_work_status)}
                                              />
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              textAlign: "center",
                                              verticalAlign: "middle",
                                              fontVariantNumeric: "tabular-nums",
                                            }}
                                          >
                                            {task.employee_number_from}-{task.employee_number_to}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                justifyContent: "center",
                                                alignItems: "center",
                                                minWidth: 290,
                                                mx: "auto",
                                              }}
                                            >
                                              {canRunTaskByStatus(task.task_status) ? (
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  onClick={() => handleRunTask(task)}
                                                  disabled={isRunLoading || isStopLoading || isSetPendingLoading}
                                                  sx={{ minWidth: 90 }}
                                                >
                                                  Run
                                                </Button>
                                              ) : (
                                                <Button
                                                  size="small"
                                                  color="warning"
                                                  variant="contained"
                                                  onClick={() => handleStopTask(task.id)}
                                                  disabled={isRunLoading || isStopLoading || isSetPendingLoading}
                                                  sx={{ minWidth: 90 }}
                                                >
                                                  Stop
                                                </Button>
                                              )}
                                              <Button
                                                size="small"
                                                color="error"
                                                variant="text"
                                                onClick={() => setDeleteTaskId(task.id)}
                                                disabled={isTaskDeleting || isTaskRunning(task)}
                                                sx={{ minWidth: 90 }}
                                              >
                                                Delete
                                              </Button>
                                              {shouldShowPass2FA(task) && (
                                                <Button
                                                  size="small"
                                                  color="secondary"
                                                  variant="outlined"
                                                  onClick={() => {
                                                    setTwoFATask(task);
                                                    setTwoFAError(null);
                                                    setTwoFACode("");
                                                  }}
                                                  sx={{ minWidth: 90 }}
                                                >
                                                  Pass 2FA
                                                </Button>
                                              )}
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </>
            ) : isConnectionsScreen ? (
              <ConnectionsPanel
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                onBack={() => navigate(`/bots-management/${selectedUser.id}`)}
              />
            ) : routedMajorJobPk != null ? (
              <MajorJobsPanel
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                detailMajorJobId={routedMajorJobPk}
                onExitMajorDetail={() => navigate(`/bots-management/${selectedUser.id}`)}
                onOpenConnectionsScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
              />
            ) : (
              <MajorJobsPanel
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                onOpenMajorDetail={(mjId) =>
                  navigate(`/bots-management/${selectedUser.id}/major-job/${mjId}`)
                }
                onOpenConnectionsScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
              />
            )}
          </Paper>
        </Box>
      </Container>
      <Dialog
        open={deleteTaskId != null}
        onClose={() => setDeleteTaskId(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this task?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskId(null)} disabled={isTaskDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteTaskId != null && void handleDeleteTask(deleteTaskId)}
            disabled={isTaskDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={bulkDeleteTasksOpen}
        onClose={() => setBulkDeleteTasksOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete selected tasks</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently remove {bulkPickedIds.length} selected task(s)? Running tasks remain blocked server-side.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteTasksOpen(false)} disabled={isBulkTasksDeleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleBulkDeleteTasksConfirmed()} disabled={isBulkTasksDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={manageJobId != null}
        onClose={() => {
          setManageJobId(null);
          setManageJobDetail(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_NO_SPINNER_SX }}
      >
        <DialogTitle>{managedJob ? `Manage ${getJobDisplayName(managedJob)}` : "Manage Job"}</DialogTitle>
        <DialogContent>
          {managedJob && (
            <Box sx={{ display: "grid", gap: 1.5, mt: 0.5 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <StatusChip
                  label={managedJob.task_status}
                  color={taskRunStatusChipColor(managedJob.task_status)}
                />
                <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 180px", minWidth: 0 }}>
                  Bot: {managedJob.bot_name}. Range: {managedJob.employee_number_from}-
                  {managedJob.employee_number_to}.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => openJobEditor(managedJob)}>
                Edit job
              </Button>
              <Button variant="outlined" onClick={() => openAddTasksDialog(managedJob)}>
                Add tasks
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleViewJobFiles(managedJob)}
              >
                View files
              </Button>
              <Button
                variant="contained"
                onClick={() => handleRunJob(managedJob.id)}
                disabled={isBatchRunning}
              >
                Run job
              </Button>
              <Button
                color="warning"
                variant="contained"
                onClick={() => handleStopJob(managedJob.id)}
                disabled={isBatchStopping}
              >
                Stop job
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={() => setDeleteJobId(managedJob.id)}
                disabled={isJobDeleting}
              >
                Delete job
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManageJobId(null); setManageJobDetail(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editJobId != null}
        onClose={() => setEditJobId(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_NO_SPINNER_SX }}
      >
        <DialogTitle>Edit Job</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Job name"
            value={editJobName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEditJobName(e.target.value)}
            sx={{ mt: 0.5, mb: 1.5 }}
          />
          <TextField
            select
            fullWidth
            label="Bot type"
            value={editJobKind}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setEditJobKind(e.target.value);
              setEditJobBotName("");
            }}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value="download_bots">Download</MenuItem>
            <MenuItem value="upload_bots">Upload</MenuItem>
          </TextField>
          <Autocomplete
            fullWidth
            openOnFocus
            autoHighlight
            options={registryVendorsAll}
            value={editJobRegistryVendor || null}
            onChange={(_, value) => {
              setEditJobRegistryVendor(value ?? "");
              setEditJobBotName("");
            }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(a, b) => a === b}
            filterOptions={(options, state) => filterRegistryVendors(options, state)}
            ListboxProps={BATCH_DIALOG_LISTBOX_PROPS}
            renderInput={(params) => (
              <TextField {...params} required label="Vendor" sx={{ mb: 1.5 }} />
            )}
          />
          <Autocomplete
            fullWidth
            openOnFocus
            autoHighlight
            disabled={!editJobRegistryVendor}
            options={editJobBotOptions}
            value={selectedEditJobBot}
            onChange={(_, option) => setEditJobBotName(option ? botNameFromRegistry(option) : "")}
            getOptionLabel={(option) => option.bot_slug}
            isOptionEqualToValue={(a, b) => a.bot_id === b.bot_id}
            filterOptions={(options, state) => filterRegistryBots(options, state)}
            ListboxProps={BATCH_DIALOG_LISTBOX_PROPS}
            noOptionsText={editJobRegistryVendor ? "No bots for this vendor and type" : "Select a vendor first"}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Bot"
                sx={{ mb: 1.5 }}
                helperText="All tasks inside this job use this bot type."
              />
            )}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Additional options"
            value={editJobAdditionalOptions}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEditJobAdditionalOptions(e.target.value)}
            helperText="Saved on the job and copied to all job tasks."
            sx={{ mb: 1.5 }}
          />
          {(editJobKind === "download_bots" || editJobKind === "upload_bots") && (
            <FormControlLabel
              sx={{ mb: 1.5 }}
              control={
                <Switch
                  checked={editJobProcessErrors}
                  onChange={(e) => setEditJobProcessErrors(e.target.checked)}
                />
              }
              label="Process error employees"
            />
          )}
          <TextField
            fullWidth
            label="Employee To"
            value={editJobEmployeeTo}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditJobEmployeeTo(sanitizeEmployeesInput(e.target.value))
            }
            helperText={`Inclusive upper employee index (starts from ${editJobBaselineFrom}).`}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={{ mb: 1.5 }}
          />
          {editJobError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {editJobError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditJobId(null)} disabled={isJobSaving}>
            Cancel
          </Button>
          <Button onClick={handleSaveJob} disabled={isJobSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteJobId != null}
        onClose={() => {
          setDeleteJobId(null);
          setDeleteJobLogs(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Are you sure you want to delete this job? All tasks inside it will be deleted too.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={deleteJobLogs}
                onChange={(_, checked) => setDeleteJobLogs(checked)}
              />
            }
            label="Delete job logs and artifacts from S3"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteJobId(null);
              setDeleteJobLogs(false);
            }}
            disabled={isJobDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteJobId != null && void handleDeleteJob(deleteJobId)}
            disabled={isJobDeleting}
          >
            {isJobDeleting ? <CircularProgress size={22} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={twoFATask != null}
        onClose={() => setTwoFATask(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>2FA Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {twoFATask
              ? `Task #${twoFATask.id} (${twoFATask.bot_name}, employees ${twoFATask.employee_number_from}-${twoFATask.employee_number_to}) requires a 2FA code.`
              : "Task requires a 2FA code."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="2FA Code"
            value={twoFACode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setTwoFACode(e.target.value)}
            error={Boolean(twoFAError)}
            helperText={twoFAError || " "}
            disabled={isSubmit2FALoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFATask(null)} disabled={isSubmit2FALoading}>
            Close
          </Button>
          <Button onClick={handleSubmit2FA} disabled={isSubmit2FALoading}>
            Submit code
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editConnectionId != null}
        onClose={() => setEditConnectionId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Connection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Port is fixed at 9223 for bot connections.
          </Typography>
          <TextField
            fullWidth
            label="Vendor"
            value={editConnectionVendor}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditConnectionVendor(e.target.value)
            }
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Host (optional)"
            value={editConnectionHost}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditConnectionHost(e.target.value)
            }
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Username"
            value={editConnectionUsername}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditConnectionUsername(e.target.value)
            }
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Client Code"
            value={editConnectionClientCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditConnectionClientCode(e.target.value)
            }
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Password"
            type={isConnectionPasswordVisible ? "text" : "password"}
            value={editConnectionPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditConnectionPassword(e.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setIsConnectionPasswordVisible((prev) => !prev)}
                    aria-label="toggle password visibility"
                  >
                    {isConnectionPasswordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Password is masked by default. Use "Show password" to view and edit.
          </Typography>
          {editConnectionError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {editConnectionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditConnectionId(null);
              setIsConnectionPasswordVisible(false);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveConnection} disabled={isConnectionSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addTasksOpen}
        onClose={() => setAddTasksOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: DIALOG_PAPER_NO_SPINNER_SX }}
      >
        <DialogTitle>
          Add tasks
          {addTasksSelectedJob ? ` — ${getJobDisplayName(addTasksSelectedJob)}` : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            New tasks copy settings from this job (same bot, folders, zip options). Employee ranges redistribute
            across all tasks when the job has automatic range recalculation enabled.
          </Typography>
          <TextField
            fullWidth
            required
            label="Number of tasks to add"
            value={addTasksCount}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setAddTasksCount(e.target.value.replace(/\D/g, ""))
            }
            placeholder="1"
            sx={{ mb: 0.5, mt: 0.25 }}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Allowed: 1–30 per request (same limit as batch create).
          </Typography>
          {addTasksError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {addTasksError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTasksOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTasks} disabled={isAddingTasks}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createBatchOpen}
        onClose={() => setCreateBatchOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_NO_SPINNER_SX }}
      >
        <DialogTitle>Create Job Batch</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            label="Job name"
            value={batchName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setBatchName(e.target.value)}
            sx={{ mt: 0.5, mb: 1.5 }}
          />
          <TextField
            select
            fullWidth
            required
            label="Bot type"
            value={batchJobKind}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setBatchJobKind(e.target.value);
              setBatchBotName("");
            }}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value="download_bots">Download</MenuItem>
            <MenuItem value="upload_bots">Upload</MenuItem>
          </TextField>
          <Autocomplete
            fullWidth
            openOnFocus
            autoHighlight
            options={registryVendorsAll}
            value={batchRegistryVendor || null}
            onChange={(_, value) => {
              setBatchRegistryVendor(value ?? "");
              setBatchBotName("");
            }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(a, b) => a === b}
            filterOptions={(options, state) => filterRegistryVendors(options, state)}
            ListboxProps={BATCH_DIALOG_LISTBOX_PROPS}
            renderInput={(params) => (
              <TextField {...params} required label="Vendor" sx={{ mb: 1.5 }} />
            )}
          />
          <Autocomplete
            fullWidth
            openOnFocus
            autoHighlight
            disabled={!batchRegistryVendor}
            options={batchBotOptions}
            value={selectedBatchBot}
            onChange={(_, option) => setBatchBotName(option ? botNameFromRegistry(option) : "")}
            getOptionLabel={(option) => option.bot_slug}
            isOptionEqualToValue={(a, b) => a.bot_id === b.bot_id}
            filterOptions={(options, state) => filterRegistryBots(options, state)}
            ListboxProps={BATCH_DIALOG_LISTBOX_PROPS}
            noOptionsText={batchRegistryVendor ? "No bots for this vendor and type" : "Select a vendor first"}
            renderInput={(params) => (
              <TextField {...params} required label="Bot" sx={{ mb: 1.5 }} />
            )}
          />
          <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
            <TextField
              fullWidth
              label="Tasks number"
              value={batchTasksNumber}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBatchTasksNumber(e.target.value.replace(/\D/g, ""))
              }
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
            <TextField
              fullWidth
              label="Inclusive last employee index"
              value={batchEmployeesNumber}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBatchEmployeesNumber(sanitizeEmployeesInput(e.target.value))
              }
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Additional options"
            value={batchAdditionalOptions}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setBatchAdditionalOptions(e.target.value)}
            sx={{ mt: 1.5 }}
          />
          {(batchJobKind === "download_bots" || batchJobKind === "upload_bots") && (
            <FormControlLabel
              sx={{ mt: 1.5 }}
              control={
                <Switch
                  checked={batchProcessErrors}
                  onChange={(e) => setBatchProcessErrors(e.target.checked)}
                />
              }
              label="Process error employees"
            />
          )}
          {batchError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {batchError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBatchOpen(false)} disabled={isBatchCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreateBatch} disabled={isBatchCreating}>
            {isBatchCreating ? <CircularProgress size={22} /> : "Create Job"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
